import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import TabSwitch from '../../../shared/components/TabSwitch';
import {
  fetchLair, fetchMissions, unlockFarm, assignFarmHero, removeFarmHero, claimFarm, startMission, claimMissionRun,
  clearLastClaimedRun, clearLairErrors, selectLair, selectMissions,
} from '../store/lairSlice';
import FarmHeroSheet from '../components/FarmHeroSheet';
import { showAdAndGrant } from '../../../shared/utils/adsgram';
import MissionsTab from '../components/MissionsTab';
import LairTab from '../components/LairTab';
import ArenaTab from '../components/ArenaTab';
import { interactiveRunPath } from '../components/MissionsTab';
import LivesChip from '../components/LivesChip';
import SendHeroSheet from '../components/SendHeroSheet';
import MissionResultSheet from '../components/MissionResultSheet';
import { BackIcon } from '../components/LairIcons';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import { formatAmount } from '../utils/missionMath';
import { buildingName, resolveAssetUrl } from '../utils/lairAssets';
import '../styles/lair.css';
import { useTranslation } from 'react-i18next';
import { entityText } from '../../../i18n/entityText';

const TABS = [
  { key: 'Missions', label: 'Missions' },
  { key: 'Lair', label: 'Lair' },
  { key: 'Arena', label: '⚔ Arena' },
];

const tabLabels = (t) => ({
  Missions: t('lair.tab.missions', 'Missions'),
  Lair: t('lair.tab.lair', 'Lair'),
  Arena: `⚔ ${t('lair.tab.arena', 'Arena')}`,
});

const TOAST_MS = 2200;

// unwrap() of a thunk skipped by its `condition` (double tap) rejects with ConditionError — ignore it
const errorText = (e, fallback) => {
  if (!e || e.name === 'ConditionError') return null;
  return typeof e === 'string' ? e : (e.message || fallback);
};

/**
 * Lair screen: Missions (games + timed expeditions; coins by hero level + DNA) |
 * Lair (coin farms unlocked for coins + DNA, worked by heroes, v1.47.0) | Arena (PvP).
 * Opened with optional `location.state.tab` = 'Missions' | 'Lair'.
 */
export default function LairPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(
    () => (TABS.some((tab) => tab.key === location.state?.tab) ? location.state.tab : 'Missions')
  );
  const [sheetMission, setSheetMission] = useState(null);
  const [farmSheet, setFarmSheet] = useState(null);
  // rewarded ad in flight (v1.47.0): finish a farm cycle
  const [adFarmId, setAdFarmId] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const lair = useSelector(selectLair);
  const missions = useSelector(selectMissions);

  const showToast = useCallback((text, kind = 'ok') => {
    clearTimeout(toastTimer.current);
    setToast({ text, kind, id: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Initial load: both tabs (resources strip needs the lair state on the Missions tab too)
  useEffect(() => {
    dispatch(fetchLair());
    dispatch(fetchMissions());
    // back from a mini-game / PvP match: PLAY_GAMES / PLAY_PVP tasks may be claimable now
    dispatch(fetchNotifications());
  }, [dispatch]);

  // Refresh the active tab's data when switching
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'Lair') dispatch(fetchLair());
    else if (key === 'Missions') dispatch(fetchMissions());
  };

  // ── Missions ──────────────────────────────────────────────────────────────
  const handleSend = async (characterId) => {
    if (!sheetMission) return false;
    try {
      const run = await dispatch(startMission({ missionId: sheetMission.id, characterId })).unwrap();
      // interactive missions (v1.41.0): open the game page of the new run
      const path = run && interactiveRunPath({ ...run, missionType: run.missionType || run.interactive || sheetMission.missionType });
      if (path) {
        navigate(path);
        return true;
      }
      showToast(t('lair.toast.sent', 'Hero sent on a mission'));
      return true;
    } catch (e) {
      return false; // startError is shown inside the sheet
    }
  };

  const handleClaim = async (runId) => {
    try {
      await dispatch(claimMissionRun(runId)).unwrap();
    } catch (e) {
      const msg = errorText(e, t('lair.toast.claim_failed', 'Failed to claim'));
      if (msg) showToast(msg, 'error');
    }
  };

  const closeSheet = () => {
    setSheetMission(null);
    dispatch(clearLairErrors());
  };

  // Rewarded ad → server grants (LAIR_FARM) → refetch
  const watchAd = async (rewardType, targetId, setBusy, refetch, okText) => {
    setBusy(targetId);
    try {
      const granted = await showAdAndGrant(rewardType, {
        targetId,
        onNoAd: () => showToast(t('ads.no_ad', 'No ad available right now, try later'), 'error'),
      });
      if (granted) {
        await dispatch(refetch());
        showToast(okText);
        // WATCH_ADS task counter is bumped right after the grant commits
        setTimeout(() => dispatch(fetchNotifications()), 1500);
      }
    } finally {
      setBusy(null);
    }
  };

  // ── Lair farms (v1.47.0) ──────────────────────────────────────────────────
  const runFarm = async (thunk, okText) => {
    try {
      const res = await dispatch(thunk).unwrap();
      if (okText) showToast(typeof okText === 'function' ? okText(res) : okText);
      return true;
    } catch (e) {
      const msg = errorText(e, t('common.something_wrong', 'Something went wrong'));
      if (msg) showToast(msg, 'error');
      return false;
    }
  };

  const handleUnlock = (farm) => runFarm(unlockFarm(farm.id),
    t('lair.toast.farm_open', '{{farm}} is open!', { farm: buildingName(farm, t) }));
  const handleRemoveHero = (farm) => runFarm(removeFarmHero(farm.id), t('lair.toast.hero_left', 'The hero left the farm'));
  const handleClaimFarm = (farm) => runFarm(claimFarm(farm.id),
    (res) => t('lair.toast.coins', '+{{amount}} coins', { amount: formatAmount(Number(res?.collected) || 0) }));
  const handlePickHero = async (hero) => {
    if (!farmSheet) return;
    const ok = await runFarm(assignFarmHero({ buildingId: farmSheet.id, characterId: hero.id }),
      t('lair.toast.hero_works', '{{hero}} works in {{farm}}', { hero: hero.label, farm: buildingName(farmSheet, t) }));
    if (ok) setFarmSheet(null);
  };
  const handleFarmAd = (farm) => watchAd('LAIR_FARM', farm.id, setAdFarmId, fetchLair,
    t('lair.toast.farm_ready', '{{farm}}: reward is ready', { farm: buildingName(farm, t) }));

  // only DNA is left since v1.47.0
  const resources = (lair.data?.resources || []).filter((r) => r.code === 'DNA');
  const farmsById = Object.fromEntries((lair.data?.buildings || []).map((b) => [b.id, b]));

  return (
    <div className="lair-page">
      <div className="lair-header">
        <button type="button" className="lair-back" onClick={() => navigate('/inventory')} aria-label={t('common.back', 'Back')}>
          <BackIcon />
        </button>
        <div className="lair-resources">
          {resources.map((r) => (
            <div
              key={r.id || r.code}
              className={`lair-res lair-res--${(r.rarity || 'COMMON').toLowerCase()}`}
              title={entityText(t, 'lair_resource', r.code, 'name', r.name)}
            >
              {r.imageUrl && <img src={resolveAssetUrl(r.imageUrl)} alt={entityText(t, 'lair_resource', r.code, 'name', r.name)} />}
              <span>{formatAmount(r.amount)}</span>
            </div>
          ))}
          {/* v1.51.0: lives of mini-games (come with GET /v1/missions) */}
          <LivesChip
            lives={missions.data?.lives}
            offsetMs={missions.offsetMs}
            onRefresh={() => dispatch(fetchMissions())}
          />
        </div>
      </div>

      <TabSwitch tabs={TABS.map((tab) => ({ ...tab, label: tabLabels(t)[tab.key] }))} activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="lair-scroll">
        {activeTab === 'Missions' ? (
          <MissionsTab
            data={missions.data}
            loaded={missions.loaded}
            error={missions.error}
            offsetMs={missions.offsetMs}
            claimingRunId={missions.claimingRunId}
            onSelectMission={setSheetMission}
            onClaim={handleClaim}
            onRetry={() => dispatch(fetchMissions())}
            onContinue={(run, path) => navigate(path)}
          />
        ) : activeTab === 'Arena' ? (
          <ArenaTab onOpen={() => navigate('/arena')} />
        ) : (
          <LairTab
            data={lair.data}
            loaded={lair.loaded}
            error={lair.error}
            offsetMs={lair.offsetMs}
            busyId={lair.busyId}
            adBusyId={adFarmId}
            lastClaim={lair.lastClaim}
            onUnlock={handleUnlock}
            onPickHero={setFarmSheet}
            onRemoveHero={handleRemoveHero}
            onClaim={handleClaimFarm}
            onWatchAd={handleFarmAd}
            onRetry={() => dispatch(fetchLair())}
          />
        )}
      </div>

      {sheetMission && (
        <SendHeroSheet
          mission={sheetMission}
          characters={missions.data?.characters}
          bonuses={missions.data?.bonuses}
          starting={!!missions.startingMissionId}
          error={missions.startError}
          lives={missions.data?.lives}
          offsetMs={missions.offsetMs}
          onSend={handleSend}
          onClose={closeSheet}
          onGetLives={() => navigate('/shop', { state: { tab: 'LIVES' } })}
          onGoToHeroes={() => navigate('/inventory', { state: { tab: 'Character' } })}
        />
      )}

      {farmSheet && (
        <FarmHeroSheet
          farm={farmsById[farmSheet.id] || farmSheet}
          heroes={lair.data?.heroes}
          farmsById={farmsById}
          busy={!!lair.busyId}
          error={null}
          onPick={handlePickHero}
          onClose={() => setFarmSheet(null)}
          onGoToHeroes={() => navigate('/inventory', { state: { tab: 'Character' } })}
        />
      )}

      {missions.lastClaimedRun && (
        <MissionResultSheet
          run={missions.lastClaimedRun}
          onClose={() => dispatch(clearLastClaimedRun())}
        />
      )}

      {toast && (
        <div key={toast.id} className={`lair-toast lair-toast--${toast.kind}`}>{toast.text}</div>
      )}
    </div>
  );
}
